import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, FileText, Upload, CheckCircle, Plus, Trash2, Pencil, Send, Download, Folder, FolderPlus, ChevronDown, X } from 'lucide-react';
import {
  uploadDocument as apiUploadDocument,
  deleteDocument as apiDeleteDocument,
  getDocumentReadUrl as apiGetDocumentReadUrl,
  setDocumentWelcomeKit as apiSetDocumentWelcomeKit,
  setDocumentKits as apiSetDocumentKits,
  renameDocument as apiRenameDocument,
  setDocumentCategory as apiSetDocumentCategory,
  moveDocument as apiMoveDocument,
  listDocFolders as apiListDocFolders,
  saveDocFolders as apiSaveDocFolders,
} from '../api.js';
import { POLICY_TYPES, getPolicyType, NON_POLICY_KITS, nonPolicyKitLabel } from '../policyTypes.js';
import { esTipoPropio, etiquetaDelTipo, tipoCanonico, idesDeFabricaPorNombre } from '../pestanasDeDocumentos.js';
import { REQUIRED_DOCS_BY_POLICY_DEFAULT } from '../policyDocs.js';
import { FileActions } from '../../deskte-file-utils.jsx';
import { toast } from './Toast.jsx';
import { DocShareModal } from './DocShareModal.jsx';
import { confirmDialog } from './ui.jsx';
import { motivoDeSubidaFallida, avisoDeArchivosFallidos } from '../motivoDeSubidaFallida.js';

const uid = () => Math.random().toString(36).substr(2, 9);

// ── Nested folders ──────────────────────────────────────────────────────────
// A folder is a PATH: "Pólizas/2026/Renovaciones". Storage stays the flat
// `folder` string on the document + the per-type name registry, so nesting
// needed no migration and never moves a blob (the folder is pure metadata).
// Mirrors normalizeFolderPath in deskte-api/src/routes/documents.routes.ts —
// keep the two in sync.
const FOLDER_SEP = '/';
const MAX_FOLDER_DEPTH = 5;
const pathSegs = (p) => String(p || '').split(FOLDER_SEP).filter(Boolean);
const folderDepth = (p) => pathSegs(p).length;
const folderLeaf = (p) => pathSegs(p).slice(-1)[0] || '';
const folderParent = (p) => pathSegs(p).slice(0, -1).join(FOLDER_SEP);
const joinFolder = (parent, name) => (parent ? `${parent}${FOLDER_SEP}${name}` : name);
// True when `path` is `root` itself or lives under it. Compared on the padded
// prefix so "Autos" never swallows "Autos Viejos".
const isUnder = (path, root) => path === root || String(path).startsWith(root + FOLDER_SEP);

// Turn a flat list of paths into a sorted tree. Intermediate levels are
// materialized even if only the deep path was ever saved, so a registry
// holding just "A/B/C" still renders A → B → C instead of one long label.
function buildFolderTree(paths) {
  const byPath = new Map();
  const ensure = (p) => {
    if (!p || byPath.has(p)) return byPath.get(p);
    const node = { path: p, name: folderLeaf(p), children: [] };
    byPath.set(p, node);
    const parent = folderParent(p);
    if (parent) ensure(parent).children.push(node);
    return node;
  };
  paths.forEach(ensure);
  const roots = [...byPath.values()].filter((n) => !folderParent(n.path));
  const sortRec = (arr) => {
    arr.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
    arr.forEach((n) => sortRec(n.children));
    return arr;
  };
  return sortRec(roots);
}

// Multi-kit picker for a document: a "Kit" button that opens a checkbox panel
// (body portal so it's never clipped) to mark the doc into several kits at once
// — each policy type, "Todos los tipos" (__all__), and "Kit Portal" (the client
// sees it in their portal). Replaces the old single-select (Lissette 2026-06-18).
function KitPicker({ doc, es, onToggle }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const kits = Array.isArray(doc.kits) ? doc.kits : [];
  const has = (id) => kits.includes(id);
  const inAny = kits.length > 0;
  const place = () => { const el = btnRef.current; if (el) setRect(el.getBoundingClientRect()); };
  useEffect(() => {
    if (!open) return;
    place();
    const reposition = () => place();
    // Capture phase so a click anywhere (even inside a modal that stops
    // bubbling) closes the panel.
    const onDown = (e) => {
      const t = e.target;
      if (!(t.closest && (t.closest('[data-kitpick]') || t.closest('[data-kitpick-btn]')))) setOpen(false);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open]);
  // Los kits que no son de un tipo de póliza van primero y juntos: el Portal y,
  // debajo, el de Cambio de Vida (Lissette 2026-08-19: "debajo de kit de
  // portal"). Ninguno de los dos convierte el documento en parte del kit de
  // bienvenida — eso lo garantiza NON_POLICY_KITS, aquí y en el backend.
  const options = [
    ...NON_POLICY_KITS.map((id) => ({ id, label: nonPolicyKitLabel(id, es) })),
    { id: '__all__', label: es ? '📦 Todos los tipos' : '📦 All types' },
    ...POLICY_TYPES.map((p) => ({ id: p.id, label: `${p.icon || ''} ${es ? (p.labelEs || p.label) : p.label}`.trim() })),
  ];
  const panel = open && rect
    ? createPortal(
        <div data-kitpick style={{ position: 'fixed', zIndex: 100000, left: Math.max(8, Math.min(rect.left, (typeof window !== 'undefined' ? window.innerWidth : 1000) - 252)), top: rect.bottom + 4, width: 244, maxHeight: 300, overflowY: 'auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,.5)', padding: '.35rem' }}>
          {options.map((o) => (
            <label key={o.id} data-kitpick style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.4rem .5rem', borderRadius: 6, cursor: 'pointer', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--c-surface3,#162d44)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              <input type="checkbox" checked={has(o.id)} onChange={(e) => onToggle(o.id, e.target.checked)} style={{ accentColor: 'var(--c-cyan,#00d4ff)', cursor: 'pointer' }} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>,
        document.body,
      )
    : null;
  return (
    <>
      <button ref={btnRef} type="button" data-kitpick-btn onClick={() => { setOpen((o) => !o); place(); }}
        title={es ? 'Elige en qué kits va este documento' : 'Choose which kits this document goes in'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', background: inAny ? 'rgba(16,185,129,.16)' : 'rgba(100,116,139,.1)', border: '1px solid ' + (inAny ? 'rgba(16,185,129,.4)' : 'var(--c-border,#1a3550)'), color: inAny ? '#34d399' : 'var(--c-text3,#45688a)', fontSize: '.72rem', borderRadius: 7, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {inAny ? '✅' : '📦'} Kit{inAny ? ` (${kits.length})` : ''} ▾
      </button>
      {panel}
    </>
  );
}
// Robust date format: backend `uploadedAt` is a FULL ISO timestamp
// (2026-06-01T14:39:14Z), while locally-added docs may be date-only
// (2026-06-01). Appending 'T00:00:00' to a full ISO string produced
// "…ZT00:00:00" → Invalid Date (Lissette 2026-06-01). Pin date-only
// strings to local midnight; parse full ISO as-is. Always en-US.
const fmt = (d) => {
  if (!d) return '--';
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
  return isNaN(dt.getTime()) ? '--' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Local calendar date as 'YYYY-MM-DD'. toISOString() is UTC, which rolls to
// tomorrow in Florida (UTC-4/-5) after ~8pm; build from local getters.
const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

// An iPhone photo saved before the upload portal started forcing JPEG. No
// browser renders HEIC, so "View" would just open a blank tab — those get
// downloaded with an explanation instead.
export const isHeicDoc = (doc) => /\.hei[cf]$/i.test(doc?.name || '') || /hei[cf]/i.test(doc?.mimeType || '');

export function DocTypeThumbnail({ doc, size = 38 }) {
  const name = (doc?.name || doc?.type || '').toLowerCase();
  const mime = (doc?.mimeType || '').toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';
  const isImage = mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  const isPdf = mime === 'application/pdf' || ext === 'pdf';
  const isWord = mime.includes('word') || ['doc', 'docx', 'rtf'].includes(ext);
  const isExcel = mime.includes('excel') || mime.includes('sheet') || ['xls', 'xlsx', 'ods'].includes(ext);
  const isCsv = mime === 'text/csv' || ext === 'csv';
  const isText = mime.startsWith('text/') || ['txt', 'md', 'log'].includes(ext);
  const isVideo = mime.startsWith('video/') || ['mp4', 'mov', 'avi', 'webm'].includes(ext);
  const isAudio = mime.startsWith('audio/') || ['mp3', 'wav', 'm4a', 'ogg'].includes(ext);
  const isZip = mime.includes('zip') || ['zip', 'rar', '7z'].includes(ext);

  // Image with real data → show thumbnail
  if (isImage && doc?.data) {
    return (
      <div style={{ width: size, height: size, borderRadius: 6, overflow: 'hidden', border: '1px solid #1a3550', flexShrink: 0, background: '#070d14' }}>
        <img src={doc.data} alt={doc.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }

  let emoji = '📄', bg = 'rgba(148,163,184,.12)', fg = '#94a3b8', border = 'rgba(148,163,184,.3)';
  if (isImage)      { emoji = '🖼️'; bg = 'rgba(236,72,153,.12)';  fg = '#ec4899'; border = 'rgba(236,72,153,.35)'; }
  else if (isPdf)   { emoji = '📕'; bg = 'rgba(239,68,68,.12)';   fg = '#ef4444'; border = 'rgba(239,68,68,.35)'; }
  else if (isWord)  { emoji = '📘'; bg = 'rgba(59,130,246,.12)';  fg = '#3b82f6'; border = 'rgba(59,130,246,.35)'; }
  else if (isExcel) { emoji = '📗'; bg = 'rgba(34,197,94,.12)';   fg = '#22c55e'; border = 'rgba(34,197,94,.35)'; }
  else if (isCsv)   { emoji = '📊'; bg = 'rgba(251,146,60,.12)';  fg = '#fb923c'; border = 'rgba(251,146,60,.35)'; }
  else if (isText)  { emoji = '📝'; bg = 'rgba(100,116,139,.12)'; fg = '#64748b'; border = 'rgba(100,116,139,.35)'; }
  else if (isVideo) { emoji = '🎥'; bg = 'rgba(168,85,247,.12)';  fg = '#a855f7'; border = 'rgba(168,85,247,.35)'; }
  else if (isAudio) { emoji = '🎵'; bg = 'rgba(6,182,212,.12)';   fg = '#06b6d4'; border = 'rgba(6,182,212,.35)'; }
  else if (isZip)   { emoji = '🗜️'; bg = 'rgba(245,158,11,.12)';  fg = '#f59e0b'; border = 'rgba(245,158,11,.35)'; }

  return (
    <div style={{ width: size, height: size, borderRadius: 6, background: bg, border: '1px solid ' + border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: fg, fontSize: size * 0.48 }}>
      {emoji}
    </div>
  );
}

export function DocFolder({ client, policies, docs, onDocsChange, eligibilityAlerts, requests = [], lang, t }) {
  const [newType, setNewType] = useState('');
  const fileRef = useRef(null);
  const [uploadDocId, setUploadDocId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [kitGuideOpen, setKitGuideOpen] = useState(false);
  // Documents-tab organization: a sub-tab bar ("General" + one per policy type
  // the client has) with named folders inside each policy-type tab. `folders`
  // is the per-client registry { [policyTypeId]: string[] } (persists empty
  // folders); a document's filing is its own folder/folderType fields.
  const [activeTab, setActiveTab] = useState('general');
  const [folders, setFolders] = useState({});
  const [openFolders, setOpenFolders] = useState({}); // `${type}::${name}` → bool
  const [newFolderName, setNewFolderName] = useState('');
  // Inline "new subfolder" input: the parent PATH it hangs off, plus its text.
  const [subFolderParent, setSubFolderParent] = useState(null);
  const [subFolderName, setSubFolderName] = useState('');
  const [renamingFolder, setRenamingFolder] = useState(null); // { type, name }
  const [renameFolderVal, setRenameFolderVal] = useState('');
  const [moveDoc, setMoveDoc] = useState(null); // doc being moved (opens picker)
  const [shareOpen, setShareOpen] = useState(false); // "Enviar por correo" modal
  const [showNewTab, setShowNewTab] = useState(false); // custom-tab creator input
  const [newTabName, setNewTabName] = useState('');
  const [folderUploadBusy, setFolderUploadBusy] = useState(null); // `${type}::${name}` uploading
  const [tabOrder, setTabOrder] = useState([]); // personal drag-reorder of tabs
  const [dragTabId, setDragTabId] = useState(null);
  const tf = t.docFolder;
  const es = lang === 'es';
  const isApiModeD = typeof window !== 'undefined' && !!window.__deskteAuth;

  // Auto-suggested line category from the client's policies: when every policy
  // shares one type, suggest it as the default for new uploads. Ambiguous
  // (0 or >1 distinct types) → no suggestion, the agent picks manually.
  const clientPolicies = (policies || []).filter(p => p.clientId === client.id);
  const distinctTypes = [...new Set(clientPolicies.map(p => p.type).filter(Boolean))];
  const suggestedCat = distinctTypes.length === 1 ? distinctTypes[0] : null;

  // Auto-sync eligibility alerts into docs
  useEffect(() => {
    const pending = (eligibilityAlerts || []).filter(a => a.clientId === client.id && a.status === 'pending');
    if (pending.length === 0) return;
    const clientDocs = docs.filter(d => d.clientId === client.id);
    const toAdd = [];
    pending.forEach(alert => {
      const docType = es ? alert.doc : alert.docEn;
      const already = clientDocs.some(d => (d.type || '').toLowerCase() === (docType || '').toLowerCase() || (d.eligAlertId && d.eligAlertId === alert.id));
      if (!already) toAdd.push({ id: uid(), clientId: client.id, name: null, type: docType, uploadedAt: null, size: null, required: true, eligAlertId: alert.id, deadline: alert.deadline, source: 'eligibility' });
    });
    if (toAdd.length > 0) onDocsChange(prev => [...prev, ...toAdd]);
  }, [eligibilityAlerts, client.id]);

  // Load this client's folder registry. In demo mode (no API) folders live only
  // in local state, which is fine — there's nothing to persist to.
  useEffect(() => {
    if (!isApiModeD) return;
    let cancelled = false;
    apiListDocFolders(client.id)
      .then(f => { if (!cancelled) setFolders(f && typeof f === 'object' ? f : {}); })
      .catch(() => { /* registry is best-effort; folders also derive from docs */ });
    return () => { cancelled = true; };
  }, [client.id]);

  const clientDocs = docs.filter(d => d.clientId === client.id);
  const missing = clientDocs.filter(d => d.required && !d.uploadedAt);

  // Welcome Kit guidance: based on the client's policy types, which document
  // TYPES are recommended for the kit (from the default per-type checklist).
  // This is a read-only suggestion layer over the manual per-doc "📦 Kit"
  // marking — it tells the agent what each policy type expects and lets them
  // one-click add an already-uploaded matching doc to that kit. Suggestions
  // only; no PHI is sent here. Accent/case-insensitive tolerant matching so a
  // file named "Identificación oficial.pdf" matches "Identificacion Oficial".
  const normK = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const docMatchesName = (d, sk) => { const dn = normK(d.name || d.type); return !!dn && !!sk && (dn.includes(sk) || sk.includes(dn)); };
  const kitSuggestions = (() => {
    const map = new Map(); // normalized display name → { name, types:Set, required }
    distinctTypes.forEach(type => {
      (REQUIRED_DOCS_BY_POLICY_DEFAULT[type] || []).forEach(d => {
        const display = es ? (d.nameEs || d.name) : d.name;
        const key = normK(display);
        if (!key) return;
        if (!map.has(key)) map.set(key, { name: display, types: new Set(), required: false });
        const entry = map.get(key);
        entry.types.add(type);
        if (d.required) entry.required = true;
      });
    });
    return [...map.values()].map(s => {
      const sk = normK(s.name);
      const match = clientDocs.find(d => d.uploadedAt && docMatchesName(d, sk)) || null;
      const inKit = !!(match && match.welcomeKit);
      const types = [...s.types];
      return { ...s, types, match, status: !match ? 'missing' : (inKit ? 'in_kit' : 'uploaded') };
    });
  })();
  const kitInCount = kitSuggestions.filter(s => s.status === 'in_kit').length;

  // Outstanding requested documents ("solicitudes"). Computed read-only from the
  // document requests so the agent sees, right here in the client's Documents
  // tab, what was asked for and is still awaiting upload. The request status is
  // the source of truth (pending/partial/expired = outstanding; completed =
  // every file uploaded → hidden). We don't mutate `docs` here to avoid creating
  // phantom rows that would duplicate the real file the client uploads via the
  // portal link.
  const requestedPending = [];
  (requests || []).filter(r => r.clientId === client.id && r.status !== 'completed').forEach(req => {
    const expired = req.status === 'expired';
    (req.docs || []).forEach(d => {
      requestedPending.push({
        name: d.name || (es ? 'Documento' : 'Document'),
        required: !!d.required,
        overdue: expired,
        sentAt: req.sentAt || null,
        link: req.link || null,
      });
    });
  });

  const handleFileUpload = async (docId, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const sizeStr = file.size < 1024 ? file.size + ' B' : file.size < 1048576 ? Math.round(file.size / 1024) + ' KB' : (file.size / 1048576).toFixed(1) + ' MB';
    if (isApiModeD) {
      try {
        // If this slot came from an eligibility alert, tag the upload with a
        // "elig:<alertId>" marker in lineCategory (a free-form, persisted field).
        // On refresh mapDocumentFromApi turns that marker back into eligAlertId,
        // so the requested-document slot RE-MATCHES the saved file instead of
        // reappearing empty (the file looked lost — Lissette 2026-06-30,
        // MariaAlejandra agency). Without it the upload persisted as a loose
        // "other" doc with no link back to the slot.
        const slot = docs.find(d => d.id === docId);
        const eligMarker = slot && slot.source === 'eligibility' && slot.eligAlertId ? `elig:${slot.eligAlertId}` : null;
        const created = await apiUploadDocument(file, { clientId: client.id, category: 'other', lineCategory: eligMarker || suggestedCat });
        // Fill the requested-doc slot, then collapse any duplicate the SSE
        // document.created echo may have appended with the same server id
        // (feedback_sse_echo_dedup_optimistic_add — slot-fill variant).
        onDocsChange(prev => {
          const mapped = prev.map(d => d.id === docId ? { ...d, ...created, name: file.name, size: sizeStr, uploadedAt: created.uploadedAt || new Date().toISOString() } : d);
          return mapped.filter((d, i, arr) => arr.findIndex(z => z.id === d.id) === i);
        });
        setUploadDocId(null);
        if (fileRef.current) fileRef.current.value = '';
      } catch (err) {
        toast.error(motivoDeSubidaFallida(err, es));
      }
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      onDocsChange(prev => prev.map(d => d.id === docId ? { ...d, name: file.name, uploadedAt: new Date().toISOString(), size: sizeStr, data: ev.target.result, mimeType: file.type } : d));
      setUploadDocId(null);
      if (fileRef.current) fileRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const startRename = (doc) => { setRenamingId(doc.id); setRenameVal(doc.name || ''); };
  // Renaming a document to something readable ("LICENCIA ANA CRISTINA LOAIZA")
  // used to drop the extension, and the downloaded file then had none — Windows
  // asked which program to open it with. Keep the original extension whenever
  // the typed name doesn't already carry one.
  const KNOWN_EXT_RE = /\.(pdf|png|jpe?g|gif|webp|heic|heif|bmp|tiff?|csv|xlsx?|docx?|txt)$/i;
  const keepExt = (typed, original) => {
    if (!typed || KNOWN_EXT_RE.test(typed)) return typed;
    const ext = (String(original || '').match(KNOWN_EXT_RE) || [])[0];
    return ext ? `${typed}${ext.toLowerCase()}` : typed;
  };
  const saveRename = async (doc) => {
    const name = keepExt(renameVal.trim(), doc.name);
    setRenamingId(null);
    if (!name || name === doc.name) return;
    onDocsChange(prev => prev.map(d => d.id === doc.id ? { ...d, name } : d));
    if (isApiModeD && doc.uploadedAt) {
      try { await apiRenameDocument(doc.id, name); }
      catch (err) {
        onDocsChange(prev => prev.map(d => d.id === doc.id ? { ...d, name: doc.name } : d));
        toast.error((es ? 'Error al renombrar: ' : 'Rename failed: ') + err.message);
      }
    }
  };

  const triggerUpload = (docId) => { setUploadDocId(docId); setTimeout(() => { if (fileRef.current) fileRef.current.click(); }, 50); };
  const add = () => { if (!newType.trim()) return; onDocsChange(prev => [...prev, { id: uid(), clientId: client.id, name: null, type: newType, uploadedAt: null, size: null, required: true }]); setNewType(''); };

  // Multi-file upload (Lissette 2026-07-02): pick several files at once; each
  // becomes its own document entry (no slot needed). Sequential so one failed
  // file doesn't abort the rest; reports ok/fail totals at the end.
  const multiRef = useRef(null);
  const [multiBusy, setMultiBusy] = useState(false);
  const sizeLabel = (n) => n < 1024 ? n + ' B' : n < 1048576 ? Math.round(n / 1024) + ' KB' : (n / 1048576).toFixed(1) + ' MB';
  // Shared by the "Subir varios" picker and by drag-and-drop onto the General
  // tab (Mariu Gonzalez, 2026-08-12): both end up with a plain array of File,
  // so the upload path lives here once instead of being duplicated per entry
  // point.
  const uploadFiles = async (files) => {
    if (!files.length) return;
    if (!isApiModeD) {
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (ev) => onDocsChange(prev => [...prev, { id: uid(), clientId: client.id, name: file.name, type: file.name, uploadedAt: new Date().toISOString(), size: sizeLabel(file.size), data: ev.target.result, mimeType: file.type, required: false }]);
        reader.readAsDataURL(file);
      });
      return;
    }
    setMultiBusy(true);
    let ok = 0;
    const fallos = [];
    for (const file of files) {
      try {
        const created = await apiUploadDocument(file, { clientId: client.id, category: 'other', lineCategory: suggestedCat });
        onDocsChange(prev => {
          const next = [...prev, { ...created, clientId: client.id, name: file.name, size: sizeLabel(file.size), uploadedAt: created.uploadedAt || new Date().toISOString(), required: false }];
          // Collapse any duplicate the SSE document.created echo may have
          // appended with the same server id (feedback_sse_echo_dedup).
          return next.filter((d, i, arr) => arr.findIndex(z => z.id === d.id) === i);
        });
        ok++;
      } catch (err) { fallos.push(err); }
    }
    setMultiBusy(false);
    if (ok) toast.success(es ? `${ok} archivo(s) subido(s)` : `${ok} file(s) uploaded`);
    // Con la RAZÓN, no solo el número (E-416).
    if (fallos.length) toast.error(avisoDeArchivosFallidos(fallos, es));
  };
  const handleMultiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    // Clear before awaiting: picking the SAME file twice in a row fires no
    // change event otherwise.
    if (multiRef.current) multiRef.current.value = '';
    await uploadFiles(files);
  };

  // Drag-and-drop onto the General tab. `dragDepth` counts enter/leave pairs:
  // dragleave also fires when the pointer crosses into a CHILD element, so a
  // plain boolean makes the highlight flicker off while the file is still over
  // the zone.
  const dragDepth = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  const DROP_ACCEPT = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xlsx', 'csv', 'txt'];
  // Only react to an actual file drag. Dragging a doc row inside the CRM (or
  // selected text) must not light the zone up.
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDropZoneEnter = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDropZoneOver = (e) => {
    if (!isFileDrag(e)) return;
    // Required, or the browser opens the file instead of firing onDrop.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDropZoneLeave = (e) => {
    if (!isFileDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDropZoneDrop = async (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (!dropped.length) return;
    // Dropping a FOLDER yields a zero-byte entry with no extension rather than
    // its contents; tell the user instead of uploading an empty file.
    const accepted = dropped.filter(f => DROP_ACCEPT.includes((f.name.split('.').pop() || '').toLowerCase()));
    const rejected = dropped.length - accepted.length;
    if (rejected) toast.error(es ? `${rejected} archivo(s) omitido(s): formato no admitido` : `${rejected} file(s) skipped: unsupported format`);
    await uploadFiles(accepted);
  };

  // Multi-kit membership for a stored document (Lissette 2026-06-18). `kits` is
  // an array of kit ids: policy-type ids (Kit Vida / Salud / Pet…), "__all__"
  // (rides along with every type kit), and "portal" (visible in the client
  // portal). The backend derives welcomeKit + lineCategory from this array for
  // the renewal-kit send. Optimistic, with revert on error.
  const setDocKits = async (doc, nextArr) => {
    const prevKits = Array.isArray(doc.kits) ? doc.kits : [];
    const norm = [...new Set(nextArr)];
    const sameSet = norm.length === prevKits.length && norm.every((k) => prevKits.includes(k));
    if (sameSet) return;
    // Keep the legacy fields in sync locally so the rest of the UI (welcome-kit
    // suggestions) stays consistent immediately.
    const typeKits = norm.filter((k) => k !== 'portal');
    const wk = typeKits.length > 0;
    const lc = (typeKits.length === 1 && typeKits[0] !== '__all__') ? typeKits[0] : null;
    onDocsChange(prev => prev.map(d => d.id === doc.id ? { ...d, kits: norm, welcomeKit: wk, lineCategory: lc } : d));
    if (isApiModeD && doc.uploadedAt) {
      try { await apiSetDocumentKits(doc.id, norm); }
      catch (err) {
        onDocsChange(prev => prev.map(d => d.id === doc.id ? { ...d, kits: prevKits } : d));
        toast.error((es ? 'Error: ' : 'Error: ') + err.message);
      }
    }
  };
  const toggleDocKit = (doc, kitId, checked) => {
    const cur = Array.isArray(doc.kits) ? doc.kits : [];
    setDocKits(doc, checked ? [...cur, kitId] : cur.filter((k) => k !== kitId));
  };

  // ── Folder organization ────────────────────────────────────────────────────
  // Un documento vive en UNA pestaña: General si no tiene tipo, y si no, la de
  // su tipo. Dentro de esa pestaña puede estar suelto o dentro de una carpeta.
  //
  // Antes hacian falta LAS DOS COSAS —tipo Y carpeta— para contar como
  // archivado, asi que un PDF con su tipo puesto pero sin carpeta se quedaba en
  // General y su pestaña marcaba 0. Pomares Insurance Group, 2026-09-18:
  // *"subimos en el area de documentos los pdf correspondientes tanto en las
  // polizas de vida como en las de salud. Cuando las buscamos nuevamente en el
  // perfil del cliente nos sale en CERO, como que nunca se ha subido nada"*.
  // La carpeta es para ORGANIZAR dentro de la pestaña; no puede ser el requisito
  // para pertenecer a ella (E-290).
  const generalDocs = clientDocs.filter(d => !d.folderType);
  const enSuPestana = clientDocs.filter(d => d.folderType);
  // Los que estan dentro de una carpeta: lo que dibuja el arbol de carpetas.
  const filedDocs = clientDocs.filter(d => d.folder && d.folderType);

  // ── Tab-label canonicalization ──────────────────────────────────────────
  // A custom policy type OR a custom tab whose visible name matches a BUILT-IN
  // policy type must not open a SECOND same-named tab. Maricarmen's agency had a
  // client with a health policy on the built-in "ACA / Marketplace" type and
  // another on a same-named custom type → two identical "ACA / Marketplace" tabs
  // (2026-07-27). We collapse the custom one onto the built-in id for DISPLAY &
  // COUNTING only: stored folderType values are never rewritten here, so no
  // document is hidden or moved — filed docs simply render/count under the
  // canonical tab and heal to it on their next "Mover a".
  //
  // Las tres funciones viven en ../pestanasDeDocumentos.js: fuera del componente
  // se pueden ejecutar en una prueba sin arrastrar React, y la regla queda en un
  // solo sitio.
  const isCustom = esTipoPropio;
  const typeLabel = (typeId) => etiquetaDelTipo(typeId, es);
  const canonicalType = (id) => tipoCanonico(id, es);

  // Policy-type tabs: the types the client actually has, plus any type that
  // already owns a folder or a filed document (so nothing is hidden if a policy
  // is later removed). distinctTypes first to keep a stable order. Canonicalized
  // so same-named custom/built-in types share ONE tab.
  const typeTabIds = [...new Set([
    ...distinctTypes,
    ...Object.keys(folders || {}),
    ...enSuPestana.map(d => d.folderType).filter(Boolean),
  ].map(canonicalType).filter(Boolean))];

  // Drag-to-reorder the tabs. The chosen order is a personal UI preference kept
  // per-client in localStorage (like the sticky notes); unknown/new tabs append
  // at the end (Lissette 2026-07-14 — poder mover y ordenar las pestañas).
  const tabOrderKey = `dkt_doctaborder_${client.id}`;
  useEffect(() => {
    try { const raw = localStorage.getItem(tabOrderKey); setTabOrder(Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : []); }
    catch { setTabOrder([]); }
  }, [client.id]);
  const persistTabOrder = (next) => { setTabOrder(next); try { localStorage.setItem(tabOrderKey, JSON.stringify(next)); } catch { /* ignore quota */ } };
  const orderedTypeTabIds = (() => {
    const inOrder = (tabOrder || []).filter(id => typeTabIds.includes(id));
    const rest = typeTabIds.filter(id => !inOrder.includes(id));
    return [...inOrder, ...rest];
  })();
  const onTabDrop = (targetId) => {
    const dragged = dragTabId;
    setDragTabId(null);
    if (!dragged || dragged === targetId) return;
    const cur = orderedTypeTabIds;
    const from = cur.indexOf(dragged), to = cur.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...cur];
    next.splice(from, 1);
    next.splice(to, 0, dragged);
    persistTabOrder(next);
  };

  // (isCustom / typeLabel / canonicalType are defined above, before typeTabIds.)
  // All doc filtering below matches on the CANONICAL type so a same-named custom
  // type's documents show under the built-in tab (no doc is hidden).
  const docsInFolder = (type, name) => filedDocs.filter(d => canonicalType(d.folderType) === type && d.folder === name);
  // Documents sitting in this folder OR anywhere below it — what the badge on a
  // collapsed parent should show, otherwise a folder whose files all live in
  // subfolders reads as empty.
  const docsInSubtree = (type, path) => filedDocs.filter(d => canonicalType(d.folderType) === type && (d.folder === path || String(d.folder || '').startsWith(path + FOLDER_SEP)));
  // Cuenta TODO lo de la pestaña —suelto y encarpetado—, que es lo que el
  // agente cuenta con los ojos cuando la abre.
  const docCountForType = (type) => enSuPestana.filter(d => canonicalType(d.folderType) === type).length;
  /** Los de esta pestaña que aun no estan en ninguna carpeta. */
  const sueltosEnPestana = (type) => enSuPestana.filter(d => canonicalType(d.folderType) === type && !d.folder);
  // Folders shown for a type = registry names ∪ names already used by documents,
  // unioned across every id that canonicalizes to this tab (built-in + any
  // same-named custom ids).
  const foldersForType = (type) => {
    const reg = Object.keys(folders || {})
      .filter(k => canonicalType(k) === type)
      .flatMap(k => (Array.isArray(folders[k]) ? folders[k] : []));
    const used = filedDocs.filter(d => canonicalType(d.folderType) === type).map(d => d.folder);
    const seen = new Set(); const out = [];
    [...reg, ...used].forEach(n => { const k = (n || '').toLowerCase(); if (n && !seen.has(k)) { seen.add(k); out.push(n); } });
    return out;
  };

  // Persist the registry (optimistic; revert on error).
  const persistFolders = async (next) => {
    const prev = folders;
    setFolders(next);
    if (isApiModeD) {
      try { await apiSaveDocFolders(client.id, next); }
      catch (err) { setFolders(prev); toast.error((es ? 'Error al guardar carpetas: ' : 'Failed to save folders: ') + err.message); }
    }
  };

  // Create a folder, optionally nested under `parent` (a full path). The name
  // is a single level: "/" is reserved as the path separator, so it can't be
  // part of a folder name or the tree would split where the user didn't mean.
  const createFolder = (type, parent = '') => {
    const name = (parent ? subFolderName : newFolderName).trim();
    if (!name) return;
    // From the inline "new subfolder" box the name is ONE level: the parent is
    // already fixed, so a "/" there would silently create two levels. From the
    // top box a path like "Pólizas/2026" is allowed as a shortcut.
    if (parent && name.includes(FOLDER_SEP)) { toast.error(es ? 'El nombre de la subcarpeta no puede contener "/".' : 'A subfolder name can\'t contain "/".'); return; }
    const addedDepth = parent ? 1 : pathSegs(name).length;
    if (folderDepth(parent) + addedDepth > MAX_FOLDER_DEPTH) { toast.error(es ? `Máximo ${MAX_FOLDER_DEPTH} niveles de carpetas.` : `Maximum folder depth is ${MAX_FOLDER_DEPTH}.`); return; }
    const path = joinFolder(parent, pathSegs(name).join(FOLDER_SEP));
    if (foldersForType(type).some(n => n.toLowerCase() === path.toLowerCase())) { toast.error(es ? 'Ya existe una carpeta con ese nombre.' : 'A folder with that name already exists.'); return; }
    if (parent) { setSubFolderName(''); setSubFolderParent(null); } else { setNewFolderName(''); }
    persistFolders({ ...folders, [type]: [...(Array.isArray(folders[type]) ? folders[type] : []), path] });
    // Open the new folder AND every ancestor, so it's visible immediately.
    setOpenFolders(o => {
      const next = { ...o, [`${type}::${path}`]: true };
      pathSegs(parent).forEach((_, i) => { next[`${type}::${pathSegs(parent).slice(0, i + 1).join(FOLDER_SEP)}`] = true; });
      return next;
    });
  };

  // Update a document's filing locally + on the server (optimistic, revert).
  const fileDoc = async (doc, folderType, folder) => {
    const prevType = doc.folderType || null, prevFolder = doc.folder || null;
    onDocsChange(prev => prev.map(d => d.id === doc.id ? { ...d, folderType: folderType || null, folder: folder || null } : d));
    if (isApiModeD && doc.uploadedAt) {
      try { await apiMoveDocument(doc.id, { folderType, folder }); }
      catch (err) { onDocsChange(prev => prev.map(d => d.id === doc.id ? { ...d, folderType: prevType, folder: prevFolder } : d)); toast.error((es ? 'Error al mover: ' : 'Move failed: ') + err.message); throw err; }
    }
  };

  const moveToFolder = async (doc, type, name) => {
    setMoveDoc(null);
    if (!type) return;
    // Sin carpeta: el documento pasa a la pestaña y se queda suelto dentro. Se
    // sale antes de tocar el registro de carpetas, que no tiene nada que
    // apuntar (Pomares, 2026-09-18 — E-290).
    if (!name) { try { await fileDoc(doc, type, null); } catch { /* reverted + toasted */ } return; }
    if (!foldersForType(type).some(n => n.toLowerCase() === name.toLowerCase())) {
      persistFolders({ ...folders, [type]: [...(Array.isArray(folders[type]) ? folders[type] : []), name] });
    }
    try { await fileDoc(doc, type, name); } catch { /* reverted + toasted in fileDoc */ }
  };

  const moveToGeneral = async (doc) => { setMoveDoc(null); try { await fileDoc(doc, null, null); } catch { /* handled in fileDoc */ } };

  // Rename renames only the LAST segment; the folder keeps its place in the
  // tree. Every descendant path and every document below it is rewritten by
  // prefix, otherwise the subtree would be orphaned under a name that no
  // longer exists.
  const renameFolder = async (type, oldPath) => {
    const leaf = renameFolderVal.trim();
    setRenamingFolder(null);
    if (!leaf || leaf === folderLeaf(oldPath)) return;
    if (leaf.includes(FOLDER_SEP)) { toast.error(es ? 'El nombre de la carpeta no puede contener "/".' : 'A folder name can\'t contain "/".'); return; }
    const newPath = joinFolder(folderParent(oldPath), leaf);
    if (foldersForType(type).some(n => n.toLowerCase() === newPath.toLowerCase() && n.toLowerCase() !== oldPath.toLowerCase())) { toast.error(es ? 'Ya existe una carpeta con ese nombre.' : 'A folder with that name already exists.'); return; }
    const rewrite = (p) => (isUnder(p, oldPath) ? newPath + String(p).slice(oldPath.length) : p);
    const reg = Array.isArray(folders[type]) ? folders[type] : [];
    persistFolders({ ...folders, [type]: [...new Set(reg.map(rewrite))] });
    const affected = filedDocs.filter(d => d.folderType === type && isUnder(String(d.folder || ''), oldPath));
    onDocsChange(prev => prev.map(d => (d.folderType === type && isUnder(String(d.folder || ''), oldPath)) ? { ...d, folder: rewrite(d.folder) } : d));
    if (isApiModeD) { for (const d of affected) { if (d.uploadedAt) { try { await apiMoveDocument(d.id, { folderType: type, folder: rewrite(d.folder) }); } catch { /* best-effort */ } } } }
    setOpenFolders(o => {
      const next = { ...o };
      Object.keys(o).forEach(k => {
        const pfx = `${type}::`;
        if (!k.startsWith(pfx)) return;
        const p = k.slice(pfx.length);
        if (isUnder(p, oldPath)) { next[`${pfx}${rewrite(p)}`] = o[k]; delete next[k]; }
      });
      return next;
    });
  };

  // Deleting a folder deletes its SUBTREE. Documents anywhere inside move to
  // General — nothing is ever deleted — and the confirm spells out both counts
  // so nobody nukes three levels thinking they clicked one empty folder.
  const deleteFolder = async (type, name) => {
    const inside = docsInSubtree(type, name);
    const subCount = foldersForType(type).filter(n => n !== name && isUnder(n, name)).length;
    const label = folderLeaf(name);
    const ok = await confirmDialog({
      title: es ? 'Eliminar carpeta' : 'Delete folder',
      message: (inside.length > 0 || subCount > 0)
        ? (es
            ? `La carpeta "${label}" tiene ${subCount} subcarpeta(s) y ${inside.length} documento(s) en total. Se elimina la carpeta y sus subcarpetas; los documentos se moverán a General (no se borra ningún documento). ¿Continuar?`
            : `Folder "${label}" has ${subCount} subfolder(s) and ${inside.length} document(s) in total. The folder and its subfolders are removed; the documents move to General (no document is deleted). Continue?`)
        : (es ? `¿Eliminar la carpeta vacía "${label}"?` : `Delete the empty folder "${label}"?`),
      confirmLabel: es ? 'Eliminar carpeta' : 'Delete folder',
      cancelLabel: es ? 'Cancelar' : 'Cancel',
      danger: true,
    });
    if (!ok) return;
    onDocsChange(prev => prev.map(d => (d.folderType === type && isUnder(String(d.folder || ''), name)) ? { ...d, folder: null, folderType: null } : d));
    if (isApiModeD) { for (const d of inside) { if (d.uploadedAt) { try { await apiMoveDocument(d.id, { folderType: null, folder: null }); } catch { /* best-effort */ } } } }
    const reg = (Array.isArray(folders[type]) ? folders[type] : []).filter(n => !isUnder(n.toLowerCase(), name.toLowerCase()));
    const nextFolders = { ...folders };
    // A custom tab keeps existing even with zero folders (only "Delete tab"
    // removes it); a policy-type tab drops out of the registry when emptied.
    if (reg.length > 0 || isCustom(type)) nextFolders[type] = reg; else delete nextFolders[type];
    persistFolders(nextFolders);
  };

  // ── Custom tabs (agent-defined, not tied to a policy type) ──────────────────
  const createCustomTab = () => {
    const name = newTabName.trim();
    if (!name) return;
    const id = 'custom:' + name;
    const key = name.toLowerCase();
    // Los nombres de fabrica salen del mismo mapa que usa tipoCanonico. Aqui
    // se leia `_builtinIdByLabel`, una variable que se quedo en
    // deskte-full.jsx al sacar DocFolder a su archivo: al pulsar Enter la
    // funcion reventaba con ReferenceError y la pestaña no se creaba
    // (Marivy Romero, 2026-09-25).
    const deFabrica = idesDeFabricaPorNombre(es);
    // Reject a name that collides (case-insensitive) with General, a built-in
    // policy type, OR any tab already showing — not just an exact id match — so
    // a custom tab can never duplicate an existing tab's visible name. Jump to
    // the existing tab instead.
    const existingId = key === 'general' ? 'general'
      : (key in deFabrica) ? deFabrica[key]
      : typeTabIds.find(t => String(typeLabel(t) || '').trim().toLowerCase() === key);
    if (existingId) { toast.error(es ? 'Ya existe esa pestaña.' : 'That tab already exists.'); setActiveTab(existingId); setShowNewTab(false); setNewTabName(''); return; }
    persistFolders({ ...folders, [id]: [] });
    setActiveTab(id); setShowNewTab(false); setNewTabName('');
  };
  const deleteCustomTab = async (type) => {
    const inside = filedDocs.filter(d => d.folderType === type);
    const ok = await confirmDialog({
      title: es ? 'Eliminar pestaña' : 'Delete tab',
      message: inside.length > 0
        ? (es ? `La pestaña "${typeLabel(type)}" tiene ${inside.length} documento(s). Se moverán a General (no se borra ningún documento). ¿Continuar?` : `Tab "${typeLabel(type)}" has ${inside.length} document(s). They will move to General (no document is deleted). Continue?`)
        : (es ? `¿Eliminar la pestaña vacía "${typeLabel(type)}"?` : `Delete the empty tab "${typeLabel(type)}"?`),
      confirmLabel: es ? 'Eliminar pestaña' : 'Delete tab', cancelLabel: es ? 'Cancelar' : 'Cancel', danger: true,
    });
    if (!ok) return;
    onDocsChange(prev => prev.map(d => d.folderType === type ? { ...d, folder: null, folderType: null } : d));
    if (isApiModeD) { for (const d of inside) { if (d.uploadedAt) { try { await apiMoveDocument(d.id, { folderType: null, folder: null }); } catch { /* best-effort */ } } } }
    const nextFolders = { ...folders }; delete nextFolders[type];
    persistFolders(nextFolders);
    setActiveTab('general');
  };

  // Upload one or MORE files STRAIGHT into a folder (folderType+folder), instead
  // of loose in General (Lissette 2026-07-14; multi-file 07-14). Sequential so a
  // failed file doesn't abort the rest; reports ok/fail totals.
  const uploadToFolder = (type, name) => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.multiple = true;
    inp.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.csv,.txt';
    inp.onchange = async e => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      const guess = (f) => (f.name.split('.').pop() || '').toUpperCase() + ' Document';
      if (!isApiModeD) {
        files.forEach(f => {
          const reader = new FileReader();
          reader.onload = ev => onDocsChange(prev => [...prev, { id: uid(), clientId: client.id, name: f.name, type: guess(f), uploadedAt: new Date().toISOString(), size: sizeLabel(f.size), required: false, data: ev.target.result, mimeType: f.type, folder: name, folderType: type }]);
          reader.readAsDataURL(f);
        });
        return;
      }
      const fk = `${type}::${name || ''}`;
      setFolderUploadBusy(fk);
      let ok = 0;
      const fallos = [];
      for (const f of files) {
        try {
          const created = await apiUploadDocument(f, { clientId: client.id, category: 'other', lineCategory: isCustom(type) ? null : type, folder: name, folderType: type });
          const merged = { ...created, clientId: client.id, name: f.name, type: guess(f), size: sizeLabel(f.size), required: false, uploadedAt: created.uploadedAt || todayLocal(), folder: name, folderType: type };
          onDocsChange(prev => prev.some(x => x.id === merged.id) ? prev.map(x => x.id === merged.id ? { ...x, ...merged } : x) : [...prev, merged]);
          ok++;
        } catch (err) { fallos.push(err); }
      }
      setFolderUploadBusy(null);
      if (ok) toast.success(es ? `${ok} archivo(s) subido(s)` : `${ok} file(s) uploaded`);
      if (fallos.length) toast.error(avisoDeArchivosFallidos(fallos, es));
    };
    inp.click();
  };

  // One document row, reused by the General list and by each open folder.
  const renderDocRow = (doc) => (
    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.65rem .85rem' }}>
      {doc.uploadedAt
        ? <DocTypeThumbnail doc={doc} size={38} />
        : <div style={{ width: 38, height: 38, borderRadius: 6, background: 'rgba(100,116,139,.1)', border: '1px dashed var(--c-border,#1a3550)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FileText size={16} style={{ color: 'var(--c-text3,#45688a)' }} /></div>
      }
      <div style={{ flex: 1, minWidth: 0 }}>
        {renamingId === doc.id
          ? <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)} onBlur={() => saveRename(doc)} onKeyDown={e => { if (e.key === 'Enter') saveRename(doc); if (e.key === 'Escape') setRenamingId(null); }} style={{ width: '100%', background: 'var(--c-surface,#0a1628)', border: '1px solid var(--c-cyan,#00d4ff)', borderRadius: 6, padding: '3px 7px', fontSize: '.82rem', color: 'var(--c-white,#eef6ff)', outline: 'none' }} />
          : <div style={{ fontSize: '.82rem', color: 'var(--c-white,#eef6ff)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name || doc.type}</div>}
        <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
          <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: '.65rem', background: doc.required ? 'rgba(239,68,68,.1)' : 'rgba(100,116,139,.1)', color: doc.required ? '#f87171' : 'var(--c-text3,#45688a)' }}>{doc.required ? tf.required : tf.optional}</span>
          {doc.source === 'eligibility' && <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: '.65rem', background: 'rgba(0,212,255,.1)', color: 'var(--c-cyan,#00d4ff)' }}>📋 {es ? 'Elegibilidad' : 'Eligibility'}</span>}
          {doc.deadline && !doc.uploadedAt && <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: '.65rem', background: 'rgba(255,183,0,.1)', color: 'var(--c-amber,#ffb700)' }}>⏰ {fmt(doc.deadline)}</span>}
          {doc.uploadedAt ? <span>{fmt(doc.uploadedAt)} . {doc.size}{doc.uploadedBy ? ` . ${es ? 'por' : 'by'} ${doc.uploadedBy}` : ''}</span> : <span style={{ color: 'var(--c-amber,#ffb700)' }}>{es ? 'Sin subir' : 'Not uploaded'}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {doc.uploadedAt && renamingId !== doc.id && <button onClick={() => startRename(doc)} title={es ? 'Renombrar' : 'Rename'} style={{ display: 'flex', alignItems: 'center', padding: '5px', background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', borderRadius: 6, transition: 'color .2s' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--c-cyan,#00d4ff)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--c-text3,#45688a)'}><Pencil size={13} /></button>}
        {doc.uploadedAt && <KitPicker doc={doc} es={es} onToggle={(kitId, checked) => toggleDocKit(doc, kitId, checked)} />}
        {!doc.uploadedAt && <button onClick={() => triggerUpload(doc.id)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.2)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.72rem', borderRadius: 7, cursor: 'pointer', transition: 'background .2s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,.25)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,212,255,.1)'}><Upload size={12} />{tf.upload}</button>}
        {doc.uploadedAt && <button onClick={() => setMoveDoc(doc)} title={es ? 'Mover a una carpeta' : 'Move to a folder'} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', background: 'rgba(124,150,255,.12)', border: '1px solid rgba(124,150,255,.3)', color: '#9db2ff', fontSize: '.72rem', borderRadius: 7, cursor: 'pointer', whiteSpace: 'nowrap' }}><Folder size={12} />{es ? 'Mover a' : 'Move to'}</button>}
        {doc.data && <FileActions
          file={{ name: doc.name || doc.type, data: doc.data, type: doc.mimeType || '', size: doc.size }}
          lang={lang}
          sendTo={client?.email || ''}
          sendSubject={es ? `${doc.name || doc.type} — ${client?.firstName || ''} ${client?.lastName || ''}` : `${doc.name || doc.type} — ${client?.firstName || ''} ${client?.lastName || ''}`}
          sendBody={es ? `Hola ${client?.firstName || ''},\n\nAdjunto el documento "${doc.name || doc.type}".\n\nSaludos,\n— Deskte CRM` : `Hi ${client?.firstName || ''},\n\nPlease find the document "${doc.name || doc.type}" attached.\n\nBest,\n— Deskte CRM`}
        />}
        {doc.uploadedAt && !doc.data && doc.blobUrl && <button onClick={async () => {
          try {
            const heic = isHeicDoc(doc);
            const url = await apiGetDocumentReadUrl(doc.id, { download: heic });
            if (heic) {
              // Browsers can't display HEIC — hand it to the OS and say why.
              const a = document.createElement('a');
              a.href = url;
              a.download = doc.name || 'foto.heic';
              a.rel = 'noopener noreferrer';
              document.body.appendChild(a);
              a.click();
              a.remove();
              toast.info(es
                ? 'Es una foto de iPhone (HEIC): el navegador no puede mostrarla, se descargó. Si Windows no la abre, instala "HEIF Image Extensions" (gratis) desde Microsoft Store.'
                : "It's an iPhone photo (HEIC): browsers can't display it, so it was downloaded. If Windows won't open it, install \"HEIF Image Extensions\" (free) from the Microsoft Store.");
              return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
          } catch (err) { toast.error((es ? 'Error al abrir: ' : 'Failed to open: ') + err.message); }
        }} title={es ? 'Ver' : 'View'} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.2)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.72rem', borderRadius: 7, cursor: 'pointer' }}><FileText size={12} />{es ? 'Ver' : 'View'}</button>}
        {doc.uploadedAt && !doc.data && doc.blobUrl && <button onClick={async () => {
          try {
            const url = await apiGetDocumentReadUrl(doc.id, { download: true });
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.name || doc.type || 'document';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            a.remove();
          } catch (err) { toast.error((es ? 'Error al descargar: ' : 'Failed to download: ') + err.message); }
        }} title={es ? 'Descargar' : 'Download'} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.2)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.72rem', borderRadius: 7, cursor: 'pointer' }}><Download size={12} />{es ? 'Descargar' : 'Download'}</button>}
        {doc.uploadedAt && !doc.data && !doc.blobUrl && <CheckCircle size={16} style={{ color: 'var(--c-green,#00ff88)' }} />}
        <button onClick={async () => {
          const ok = await confirmDialog({
            title: es ? 'Eliminar documento' : 'Delete document',
            message: es ? `¿Eliminar "${doc.name || doc.type || 'este documento'}"? Esta acción no se puede deshacer.` : `Delete "${doc.name || doc.type || 'this document'}"? This action cannot be undone.`,
            confirmLabel: es ? 'Eliminar' : 'Delete',
            cancelLabel: es ? 'Cancelar' : 'Cancel',
            danger: true,
          });
          if (!ok) return;
          if (isApiModeD && doc.uploadedAt) {
            try { await apiDeleteDocument(doc.id); }
            catch (err) { toast.error((es ? 'Error al eliminar: ' : 'Failed to delete: ') + err.message); return; }
          }
          onDocsChange(prev => prev.filter(d => d.id !== doc.id));
        }} style={{ padding: 5, background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', borderRadius: 6, transition: 'color .2s' }} onMouseEnter={e => e.currentTarget.style.color = '#f87171'} onMouseLeave={e => e.currentTarget.style.color = 'var(--c-text3,#45688a)'}><Trash2 size={14} /></button>
      </div>
    </div>
  );

  // One folder card, rendered recursively so subfolders nest inside their
  // parent's open body. Indentation comes from the nesting itself (the child
  // list is padded), so there's no depth math to keep in sync.
  const renderFolderNode = (node) => {
    const path = node.path;
    const fkey = `${activeTab}::${path}`;
    const open = !!openFolders[fkey];
    const inside = docsInFolder(activeTab, path);        // this folder only
    const total = docsInSubtree(activeTab, path).length; // incl. subfolders
    const isRen = renamingFolder && renamingFolder.type === activeTab && renamingFolder.name === path;
    const addingSub = subFolderParent === path;
    const canNest = folderDepth(path) < MAX_FOLDER_DEPTH;
    return (
      <div key={fkey} style={{ border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, overflow: 'hidden', background: 'var(--c-surface,#0a1628)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.55rem .7rem', background: 'var(--c-surface2,#112538)' }}>
          <button onClick={() => setOpenFolders(o => ({ ...o, [fkey]: !o[fkey] }))} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-white,#eef6ff)', textAlign: 'left', padding: 0 }}>
            <Folder size={15} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }} />
            {isRen
              ? <input autoFocus value={renameFolderVal} onChange={e => setRenameFolderVal(e.target.value)} onClick={e => e.stopPropagation()} onBlur={() => renameFolder(activeTab, path)} onKeyDown={e => { if (e.key === 'Enter') renameFolder(activeTab, path); if (e.key === 'Escape') setRenamingFolder(null); }} style={{ flex: 1, background: 'var(--c-surface,#0a1628)', border: '1px solid var(--c-cyan,#00d4ff)', borderRadius: 6, padding: '3px 7px', fontSize: '.82rem', color: 'var(--c-white,#eef6ff)', outline: 'none' }} />
              : <span style={{ fontSize: '.84rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>}
            {node.children.length > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', padding: '0 6px', borderRadius: 100, background: 'rgba(0,212,255,.14)', color: 'var(--c-cyan,#00d4ff)', flexShrink: 0 }}>{node.children.length} 📁</span>}
            <span title={es ? 'Documentos aquí y en subcarpetas' : 'Documents here and in subfolders'} style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', padding: '0 6px', borderRadius: 100, background: 'rgba(100,116,139,.18)', color: 'var(--c-text3,#45688a)', flexShrink: 0 }}>{total}</span>
          </button>
          {canNest && !isRen && <button onClick={() => { setSubFolderParent(addingSub ? null : path); setSubFolderName(''); setOpenFolders(o => ({ ...o, [fkey]: true })); }} title={es ? 'Nueva subcarpeta' : 'New subfolder'} style={{ padding: 5, background: 'none', border: 'none', color: addingSub ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--c-cyan,#00d4ff)'} onMouseLeave={e => e.currentTarget.style.color = addingSub ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)'}><FolderPlus size={13} /></button>}
          {!isRen && <button onClick={() => { setRenamingFolder({ type: activeTab, name: path }); setRenameFolderVal(node.name); }} title={es ? 'Renombrar carpeta' : 'Rename folder'} style={{ padding: 5, background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--c-cyan,#00d4ff)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--c-text3,#45688a)'}><Pencil size={13} /></button>}
          <button onClick={() => deleteFolder(activeTab, path)} title={es ? 'Eliminar carpeta' : 'Delete folder'} style={{ padding: 5, background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.color = '#f87171'} onMouseLeave={e => e.currentTarget.style.color = 'var(--c-text3,#45688a)'}><Trash2 size={13} /></button>
          <button onClick={() => setOpenFolders(o => ({ ...o, [fkey]: !o[fkey] }))} title={es ? 'Abrir/cerrar' : 'Open/close'} style={{ padding: 5, background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer' }}><ChevronDown size={15} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} /></button>
        </div>
        {open && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '.7rem' }}>
            {addingSub && (
              <div style={{ display: 'flex', gap: 6 }}>
                <input autoFocus value={subFolderName} onChange={e => setSubFolderName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') createFolder(activeTab, path); if (e.key === 'Escape') { setSubFolderParent(null); setSubFolderName(''); } }} placeholder={es ? `Nueva subcarpeta en ${node.name}` : `New subfolder in ${node.name}`} style={{ flex: 1, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-cyan,#00d4ff)', borderRadius: 8, padding: '6px 10px', fontSize: '.78rem', color: 'var(--c-white,#eef6ff)', outline: 'none' }} />
                <button onClick={() => createFolder(activeTab, path)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', background: 'var(--c-cyan,#00d4ff)', color: '#0a1628', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '.74rem', whiteSpace: 'nowrap' }}><FolderPlus size={13} />{es ? 'Crear' : 'Create'}</button>
                <button onClick={() => { setSubFolderParent(null); setSubFolderName(''); }} style={{ padding: '6px 10px', background: 'none', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, color: 'var(--c-text3,#45688a)', cursor: 'pointer', fontSize: '.74rem' }}>{es ? 'Cancelar' : 'Cancel'}</button>
              </div>
            )}
            {/* Subfolders first, then this folder's own documents */}
            {node.children.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: '.6rem', borderLeft: '2px solid var(--c-border,#1a3550)' }}>
                {node.children.map(child => renderFolderNode(child))}
              </div>
            )}
            {inside.length === 0
              ? (node.children.length === 0 && !addingSub
                  ? <div style={{ textAlign: 'center', padding: '1rem 0', color: 'var(--c-text3,#45688a)', fontSize: '.76rem' }}>{es ? 'Carpeta vacía — sube un archivo aquí, crea una subcarpeta o muévelo desde General con “Mover a”.' : 'Empty folder — upload a file here, create a subfolder, or move one from General with “Move to”.'}</div>
                  : null)
              : inside.map(renderDocRow)}
            {/* Upload a file STRAIGHT into this folder (not General) */}
            <button onClick={() => uploadToFolder(activeTab, path)} disabled={folderUploadBusy === fkey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 9, color: 'var(--c-cyan,#00d4ff)', fontSize: '.76rem', fontWeight: 600, cursor: folderUploadBusy === fkey ? 'wait' : 'pointer' }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--c-cyan,#00d4ff)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'}><Upload size={13} />{folderUploadBusy === fkey ? (es ? 'Subiendo…' : 'Uploading…') : (es ? 'Subir aquí' : 'Upload here')}</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '1rem' }}>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.csv" style={{ display: 'none' }} onChange={e => uploadDocId && handleFileUpload(uploadDocId, e)} />
      {missing.length > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 10, padding: '.65rem .85rem', marginBottom: '1rem', color: '#f87171', fontSize: '.8rem' }}><AlertTriangle size={16} />{tf.missing}: {missing.map(d => d.type).join(', ')}</div>}
      {requestedPending.length > 0 && (
        <div style={{ border: '1px solid rgba(255,183,0,.25)', background: 'rgba(255,183,0,.06)', borderRadius: 10, padding: '.75rem .85rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.72rem', fontWeight: 800, letterSpacing: '.04em', color: '#ffb700', marginBottom: '.5rem' }}>
            <Send size={13} />{es ? 'DOCUMENTOS SOLICITADOS (PENDIENTES)' : 'REQUESTED DOCUMENTS (PENDING)'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {requestedPending.map((rd, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', color: 'var(--c-text,#c8dff0)' }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{rd.overdue ? '🚨' : '📄'}</span>
                <span style={{ flex: 1 }}>{rd.name}{rd.required ? <span style={{ color: '#f87171', marginLeft: 4 }}>*</span> : null}</span>
                <span style={{ flexShrink: 0, fontSize: '.6rem', fontWeight: 800, fontFamily: 'var(--font-mono)', letterSpacing: '.05em', padding: '1px 7px', borderRadius: 5, background: rd.overdue ? 'rgba(255,61,107,.14)' : 'rgba(255,183,0,.16)', color: rd.overdue ? '#ff3d6b' : '#ffb700', border: `1px solid ${rd.overdue ? 'rgba(255,61,107,.3)' : 'rgba(255,183,0,.3)'}` }}>{rd.overdue ? (es ? 'VENCIDO' : 'OVERDUE') : (es ? 'SOLICITADO' : 'REQUESTED')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {clientPolicies.length > 0 && kitSuggestions.length > 0 && (
        <div style={{ border: '1px solid rgba(16,185,129,.25)', background: 'rgba(16,185,129,.06)', borderRadius: 10, marginBottom: '1rem', overflow: 'hidden' }}>
          <button onClick={() => setKitGuideOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '.7rem .85rem', background: 'none', border: 'none', cursor: 'pointer', color: '#34d399', fontSize: '.74rem', fontWeight: 800, letterSpacing: '.03em', textAlign: 'left' }}>
            <span style={{ fontSize: 14 }}>🎁</span>
            <span style={{ flex: 1 }}>{es ? 'SUGERENCIAS DEL KIT DE BIENVENIDA' : 'WELCOME KIT SUGGESTIONS'}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', padding: '1px 7px', borderRadius: 5, background: 'rgba(16,185,129,.14)', border: '1px solid rgba(16,185,129,.3)' }}>{kitInCount}/{kitSuggestions.length}</span>
            <span style={{ transform: kitGuideOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', fontSize: '.7rem' }}>▾</span>
          </button>
          {kitGuideOpen && (
            <div style={{ padding: '0 .85rem .8rem' }}>
              <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginBottom: '.6rem', lineHeight: 1.5 }}>
                {es ? 'Documentos recomendados para el kit según las pólizas de este cliente. Marca cada documento subido con "📦 Kit" para incluirlo.' : 'Recommended documents for the kit based on this client’s policies. Mark each uploaded document with "📦 Kit" to include it.'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {kitSuggestions.map((s, i) => {
                  const icon = s.status === 'in_kit' ? '✅' : (s.status === 'uploaded' ? '⚠️' : '○');
                  const badge = s.status === 'in_kit'
                    ? { txt: es ? 'EN EL KIT' : 'IN KIT', bg: 'rgba(16,185,129,.16)', fg: '#34d399', bd: 'rgba(16,185,129,.35)' }
                    : s.status === 'uploaded'
                      ? { txt: es ? 'SUBIDO — NO EN KIT' : 'UPLOADED — NOT IN KIT', bg: 'rgba(255,183,0,.16)', fg: '#ffb700', bd: 'rgba(255,183,0,.3)' }
                      : { txt: es ? 'FALTA SUBIR' : 'NOT UPLOADED', bg: 'rgba(100,116,139,.12)', fg: '#94a3b8', bd: 'rgba(100,116,139,.3)' };
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', color: 'var(--c-text,#c8dff0)' }}>
                      <span style={{ fontSize: 13, flexShrink: 0, width: 16, textAlign: 'center' }}>{icon}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {s.name}{s.required ? <span style={{ color: '#f87171', marginLeft: 4 }}>*</span> : null}
                        <span style={{ marginLeft: 6, display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {s.types.map(ty => { const pt = getPolicyType(ty); return pt ? <span key={ty} title={es ? (pt.labelEs || pt.label) : pt.label} style={{ fontSize: '.62rem', opacity: .7 }}>{pt.icon}</span> : null; })}
                        </span>
                      </span>
                      {s.status === 'uploaded' && (
                        <button onClick={() => toggleDocKit(s.match, s.types.length === 1 ? s.types[0] : '__all__', true)} title={es ? 'Agregar este documento al kit' : 'Add this document to the kit'} style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', background: 'rgba(16,185,129,.14)', border: '1px solid rgba(16,185,129,.35)', color: '#34d399', fontSize: '.66rem', fontWeight: 700, borderRadius: 6, cursor: 'pointer' }}>+ Kit</button>
                      )}
                      <span style={{ flexShrink: 0, fontSize: '.58rem', fontWeight: 800, fontFamily: 'var(--font-mono)', letterSpacing: '.04em', padding: '1px 7px', borderRadius: 5, background: badge.bg, color: badge.fg, border: `1px solid ${badge.bd}` }}>{badge.txt}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Sub-tab bar: General + one tab per policy type the client has */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: '1rem', borderBottom: '1px solid var(--c-border,#1a3550)' }}>
        {[{ id: 'general', label: es ? 'General' : 'General', icon: null, count: generalDocs.length }, ...orderedTypeTabIds.map(tid => ({ id: tid, label: typeLabel(tid), icon: (getPolicyType(tid) ? getPolicyType(tid).icon : null), count: docCountForType(tid) }))].map(tb => {
          const active = activeTab === tb.id;
          // 'General' stays pinned first; the type/custom tabs are drag-reorderable.
          const draggable = tb.id !== 'general';
          return (
            <button key={tb.id} onClick={() => setActiveTab(tb.id)}
              draggable={draggable}
              onDragStart={draggable ? (() => setDragTabId(tb.id)) : undefined}
              onDragOver={draggable ? ((e) => { e.preventDefault(); }) : undefined}
              onDrop={draggable ? (() => onTabDrop(tb.id)) : undefined}
              title={draggable ? (es ? 'Arrastra para reordenar' : 'Drag to reorder') : undefined}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid ' + (active ? 'var(--c-cyan,#00d4ff)' : (dragTabId === tb.id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)')), background: active ? 'rgba(0,212,255,.12)' : 'var(--c-surface2,#112538)', color: active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)', fontSize: '.76rem', fontWeight: 700, cursor: draggable ? 'grab' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0, opacity: dragTabId === tb.id ? 0.6 : 1 }}>
              {tb.icon ? <span style={{ fontSize: 13 }}>{tb.icon}</span> : <Folder size={13} />}
              {tb.label}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', padding: '0 6px', borderRadius: 100, background: active ? 'rgba(0,212,255,.18)' : 'rgba(100,116,139,.18)' }}>{tb.count}</span>
            </button>
          );
        })}
        {/* Custom-tab creator: an agent-defined tab not tied to any policy type */}
        {showNewTab ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <input autoFocus value={newTabName} onChange={e => setNewTabName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') createCustomTab(); if (e.key === 'Escape') { setShowNewTab(false); setNewTabName(''); } }} placeholder={es ? 'Nombre de pestaña' : 'Tab name'} style={{ width: 150, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-cyan,#00d4ff)', borderRadius: 8, padding: '5px 9px', fontSize: '.76rem', color: 'var(--c-white,#eef6ff)', outline: 'none' }} />
            <button onClick={createCustomTab} title={es ? 'Crear' : 'Create'} style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 8px', borderRadius: 8, border: 'none', background: 'var(--c-cyan,#00d4ff)', color: '#0a1628', cursor: 'pointer' }}><Plus size={14} /></button>
            <button onClick={() => { setShowNewTab(false); setNewTabName(''); }} title={es ? 'Cancelar' : 'Cancel'} style={{ display: 'inline-flex', alignItems: 'center', padding: 5, background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer' }}><X size={14} /></button>
          </div>
        ) : (
          <button onClick={() => setShowNewTab(true)} title={es ? 'Crear pestaña personalizada' : 'Create custom tab'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: '1px dashed var(--c-border,#1a3550)', background: 'transparent', color: 'var(--c-text2,#7aa3c0)', fontSize: '.76rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}><Plus size={13} />{es ? 'Pestaña' : 'Tab'}</button>
        )}
      </div>

      {activeTab === 'general' ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '1rem' }}>
            {generalDocs.length === 0 && <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{tf.noFiles}</div>}
            {generalDocs.map(renderDocRow)}
          </div>
          {/* Upload new file directly (lands in General). The wrapper is the
              drop zone: dragging files from the desktop onto it uploads them,
              which is what the button does too. */}
          <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', position: 'relative', borderRadius: 10 }}
            onDragEnter={onDropZoneEnter} onDragOver={onDropZoneOver} onDragLeave={onDropZoneLeave} onDrop={onDropZoneDrop}>
            {dragOver && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(0,212,255,.12)', border: '2px dashed var(--c-cyan,#00d4ff)', borderRadius: 10, color: 'var(--c-cyan,#00d4ff)', fontSize: '.82rem', fontWeight: 700, pointerEvents: 'none' }}>
                <Upload size={16} />{es ? 'Suelta los archivos aquí' : 'Drop the files here'}
              </div>
            )}
            <button onClick={() => {
              const inp = document.createElement('input');
              inp.type = 'file';
              inp.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.csv,.txt';
              inp.onchange = async e => {
                const f = e.target.files && e.target.files[0];
                if (!f) return;
                const sz = f.size < 1024 ? f.size + ' B' : f.size < 1048576 ? Math.round(f.size / 1024) + ' KB' : (f.size / 1048576).toFixed(1) + ' MB';
                const ext = f.name.split('.').pop() || '';
                const typeGuess = ext.toUpperCase() + ' Document';
                if (isApiModeD) {
                  try {
                    const created = await apiUploadDocument(f, { clientId: client.id, category: 'other', lineCategory: suggestedCat });
                    const merged = { ...created, clientId: client.id, name: f.name, type: typeGuess, size: sz, required: false, uploadedAt: created.uploadedAt || todayLocal() };
                    // Dedup vs the SSE document.created echo (feedback_sse_echo_dedup_optimistic_add).
                    onDocsChange(prev => prev.some(x => x.id === merged.id)
                      ? prev.map(x => x.id === merged.id ? { ...x, ...merged } : x)
                      : [...prev, merged]);
                  } catch (err) { toast.error(motivoDeSubidaFallida(err, es)); }
                  return;
                }
                const reader = new FileReader();
                reader.onload = (ev) => { onDocsChange(prev => [...prev, { id: uid(), clientId: client.id, name: f.name, type: typeGuess, uploadedAt: new Date().toISOString(), size: sz, required: false, data: ev.target.result, mimeType: f.type }]); };
                reader.readAsDataURL(f);
              };
              inp.click();
            }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 10, color: 'var(--c-text2,#7aa3c0)', fontSize: '.78rem', cursor: 'pointer', transition: 'border-color .2s' }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--c-cyan,#00d4ff)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'}><Upload size={14} />{es ? '📎 Subir Documento' : '📎 Upload Document'}<span style={{ color: 'var(--c-text3,#45688a)', fontSize: '.72rem', fontWeight: 400 }}>{es ? '· o arrastra los archivos aquí' : '· or drag files here'}</span></button>
          </div>
          {/* Manual add required type + multi-file upload */}
          <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '1rem', flexWrap: 'wrap' }}>
            <input value={newType} onChange={e => setNewType(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder={es ? 'Agregar tipo requerido' : tf.addRequired} style={{ flex: 1, minWidth: 160, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '8px 12px', fontSize: '.8rem', color: 'var(--c-white,#eef6ff)', outline: 'none' }} />
            <button onClick={add} style={{ padding: '8px 14px', background: 'var(--c-cyan,#00d4ff)', color: '#0a1628', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, transition: 'opacity .2s' }} onMouseEnter={e => e.currentTarget.style.opacity = '.8'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}><Plus size={16} /></button>
            <input ref={multiRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.csv" style={{ display: 'none' }} onChange={handleMultiUpload} />
            <button onClick={() => { if (multiRef.current) multiRef.current.click(); }} disabled={multiBusy}
              title={es ? 'Selecciona varios archivos a la vez — cada uno se guarda como un documento' : 'Pick several files at once — each is saved as its own document'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: multiBusy ? 'var(--c-surface2,#112538)' : 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 10, cursor: multiBusy ? 'wait' : 'pointer', fontWeight: 700, fontSize: '.78rem', fontFamily: 'inherit' }}>
              <Upload size={14} />{multiBusy ? (es ? 'Subiendo…' : 'Uploading…') : (es ? 'Subir varios' : 'Upload multiple')}
            </button>
            {/* Send documents OUT to a third party (a carrier) as one secure
                link. Only shows once there's something uploaded to send. */}
            {docs.some(d => d.uploadedAt) && (
              <button onClick={() => setShareOpen(true)}
                title={es ? 'Envía varios documentos a un correo externo (una aseguradora) con un solo enlace seguro' : 'Send several documents to an outside email (a carrier) as one secure link'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(16,185,129,.1)', border: '1px solid rgba(16,185,129,.35)', color: '#34d399', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: '.78rem', fontFamily: 'inherit' }}>
                <Send size={14} />{es ? 'Enviar por correo' : 'Send by email'}
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Create a named folder under this policy type */}
          <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
            <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createFolder(activeTab)} placeholder={es ? `Nueva carpeta en ${typeLabel(activeTab)} — usa "Padre/Hija" para anidar` : `New folder in ${typeLabel(activeTab)} — use "Parent/Child" to nest`} style={{ flex: 1, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '8px 12px', fontSize: '.8rem', color: 'var(--c-white,#eef6ff)', outline: 'none' }} />
            <button onClick={() => createFolder(activeTab)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--c-cyan,#00d4ff)', color: '#0a1628', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '.78rem', whiteSpace: 'nowrap' }}><FolderPlus size={15} />{es ? 'Crear carpeta' : 'Create folder'}</button>
            {isCustom(activeTab) && <button onClick={() => deleteCustomTab(activeTab)} title={es ? 'Eliminar esta pestaña' : 'Delete this tab'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', background: 'rgba(239,68,68,.1)', color: '#f87171', borderRadius: 10, border: '1px solid rgba(239,68,68,.3)', cursor: 'pointer', fontWeight: 700, fontSize: '.76rem', whiteSpace: 'nowrap' }}><Trash2 size={14} />{es ? 'Pestaña' : 'Tab'}</button>}
          </div>
          {/* Subir DIRECTAMENTE a esta pestaña. Antes no habia forma: para que un
              documento contara aqui habia que crear una carpeta, volver a
              General y usar "Mover a" — tres pasos y dos pantallas. El agente
              subia desde General, el archivo se quedaba alli, y esta pestaña
              marcaba 0 (Pomares, 2026-09-18 — E-290). */}
          <button onClick={() => uploadToFolder(activeTab, null)} disabled={folderUploadBusy === `${activeTab}::`}
            title={es ? `Los archivos quedan en ${typeLabel(activeTab)}, sin carpeta. Puedes organizarlos en carpetas despues.` : `Files land in ${typeLabel(activeTab)}, with no folder. You can organize them into folders later.`}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', marginBottom: '1rem', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 10, color: 'var(--c-text2,#7aa3c0)', fontSize: '.78rem', cursor: folderUploadBusy === `${activeTab}::` ? 'wait' : 'pointer', fontFamily: 'inherit' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--c-cyan,#00d4ff)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'}>
            <Upload size={14} />
            {folderUploadBusy === `${activeTab}::`
              ? (es ? 'Subiendo…' : 'Uploading…')
              : (es ? `📎 Subir a ${typeLabel(activeTab)}` : `📎 Upload to ${typeLabel(activeTab)}`)}
          </button>

          {/* Lo de esta pestaña que todavia no esta en ninguna carpeta. Antes
              no se dibujaba en ningun sitio de aqui. */}
          {sueltosEnPestana(activeTab).length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 .15rem .4rem', fontSize: '.68rem', fontWeight: 700, letterSpacing: '.04em', color: 'var(--c-text3,#45688a)' }}>
                <FileText size={12} />
                {es ? 'SIN CARPETA' : 'NO FOLDER'}
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', padding: '0 6px', borderRadius: 100, background: 'rgba(100,116,139,.18)' }}>{sueltosEnPestana(activeTab).length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sueltosEnPestana(activeTab).map(renderDocRow)}
              </div>
            </div>
          )}

          {foldersForType(activeTab).length === 0 && sueltosEnPestana(activeTab).length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.8rem', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 10 }}>
              {es ? `Todavía no hay documentos de ${typeLabel(activeTab)}. Súbelos con el botón de arriba, o usa “Mover a” en un documento de General. Las carpetas son para organizarlos después, no hacen falta para empezar.` : `No ${typeLabel(activeTab)} documents yet. Upload them with the button above, or use “Move to” on a document in General. Folders are for organizing them later — you don't need one to start.`}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {buildFolderTree(foldersForType(activeTab)).map(node => renderFolderNode(node))}
          </div>
        </>
      )}

      {/* clientDocs, NOT docs: `docs` is the whole agency's list and the modal
          would offer (and name) other clients' files. */}
      {shareOpen && <DocShareModal client={client} docs={clientDocs} lang={lang} onClose={() => setShareOpen(false)} />}
      {moveDoc && <MoveDocModal doc={moveDoc} types={typeTabIds} foldersForType={foldersForType} typeLabel={typeLabel} getPolicyType={getPolicyType} es={es} onClose={() => setMoveDoc(null)} onMove={(type, name) => moveToFolder(moveDoc, type, name)} onGeneral={() => moveToGeneral(moveDoc)} />}
    </div>
  );
}

// "Move to" picker: choose a policy type, then a folder (existing or new), or
// send the document back to General. Pure UI — the parent does the persistence.
function MoveDocModal({ doc, types, foldersForType, typeLabel, getPolicyType, es, onClose, onMove, onGeneral }) {
  const [type, setType] = useState(doc.folderType || (types[0] || ''));
  const [name, setName] = useState('');
  const existing = type ? foldersForType(type) : [];
  // La carpeta es OPCIONAL. Exigirla era el otro medio camino del mismo fallo:
  // un PDF que ya estaba en General no podia entrar en la pestaña de su poliza
  // sin inventarse una carpeta antes (Pomares, 2026-09-18 — E-290).
  const canMove = !!type;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(2,8,18,.66)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 440, background: 'var(--c-surface,#0a1628)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1.1rem', boxShadow: '0 18px 50px rgba(0,0,0,.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '.9rem' }}>
          <Folder size={17} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
          <div style={{ flex: 1, fontSize: '.95rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>{es ? 'Mover documento' : 'Move document'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name || doc.type}</div>
        {types.length === 0 ? (
          <div style={{ fontSize: '.8rem', color: 'var(--c-text3,#45688a)', marginBottom: '.9rem' }}>{es ? 'Este cliente no tiene pólizas todavía, así que no hay tipos para organizar. Puedes dejarlo en General.' : 'This client has no policies yet, so there are no types to organize into. You can keep it in General.'}</div>
        ) : (
          <>
            <div style={{ fontSize: '.7rem', fontWeight: 800, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '.4rem' }}>{es ? '1. Pestaña' : '1. Tab'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: '.9rem' }}>
              {types.map(tid => {
                const pt = getPolicyType(tid);
                const active = type === tid;
                return <button key={tid} onClick={() => { setType(tid); setName(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8, border: '1px solid ' + (active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'), background: active ? 'rgba(0,212,255,.14)' : 'var(--c-surface2,#112538)', color: active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)', fontSize: '.76rem', fontWeight: 600, cursor: 'pointer' }}>{pt ? pt.icon : '📁'} {typeLabel(tid)}</button>;
              })}
            </div>
            <div style={{ fontSize: '.7rem', fontWeight: 800, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '.4rem' }}>{es ? '2. Carpeta (opcional)' : '2. Folder (optional)'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: existing.length ? '.6rem' : 0 }}>
              {existing.map(fn => {
                const active = name === fn;
                const depth = fn.split(FOLDER_SEP).filter(Boolean).length - 1;
                return <button key={fn} onClick={() => setName(fn)} title={fn}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', marginLeft: depth * 14, borderRadius: 8, border: '1px solid ' + (active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'), background: active ? 'rgba(0,212,255,.14)' : 'var(--c-surface2,#112538)', color: active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)', fontSize: '.76rem', fontWeight: 600, cursor: 'pointer' }}>
                  <Folder size={12} /> {depth > 0 && <span style={{ opacity: .5 }}>↳</span>} {fn.split(FOLDER_SEP).filter(Boolean).slice(-1)[0]}
                </button>;
              })}
              {existing.length === 0 && <span style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'No hay carpetas aún. Puedes dejarlo sin carpeta, o escribir un nombre para crear una.' : 'No folders yet. You can leave it with no folder, or type a name to create one.'}</span>}
            </div>
            {/* The full path of the selected folder, so a leaf name shared by
                two branches ("2026" under Autos vs under Salud) is never
                ambiguous. */}
            {name.includes(FOLDER_SEP) && <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', marginBottom: '.4rem' }}>{name.split(FOLDER_SEP).filter(Boolean).join(' › ')}</div>}
            <input value={name} onChange={e => setName(e.target.value)} placeholder={es ? 'O escribe una carpeta nueva ("Padre/Hija" para anidar)…' : 'Or type a new folder ("Parent/Child" to nest)…'} style={{ width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '8px 12px', fontSize: '.8rem', color: 'var(--c-white,#eef6ff)', outline: 'none', marginTop: '.6rem', marginBottom: '1rem' }} />
          </>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={onGeneral} style={{ padding: '8px 12px', background: 'none', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, color: 'var(--c-text2,#7aa3c0)', fontSize: '.76rem', fontWeight: 600, cursor: 'pointer' }}>{es ? '↩ Dejar en General' : '↩ Keep in General'}</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 14px', background: 'none', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, color: 'var(--c-text2,#7aa3c0)', fontSize: '.78rem', cursor: 'pointer' }}>{es ? 'Cancelar' : 'Cancel'}</button>
            <button onClick={() => canMove && onMove(type, name.trim() || null)} disabled={!canMove} title={name.trim() ? undefined : (es ? `Queda en ${typeLabel(type)}, sin carpeta` : `Lands in ${typeLabel(type)}, with no folder`)} style={{ padding: '8px 16px', background: canMove ? 'var(--c-cyan,#00d4ff)' : 'rgba(0,212,255,.3)', color: '#0a1628', borderRadius: 9, border: 'none', cursor: canMove ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: '.78rem' }}>{name.trim() ? (es ? 'Mover aquí' : 'Move here') : (es ? `Mover a ${typeLabel(type)}` : `Move to ${typeLabel(type)}`)}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
